import {
  MDSIconButton,
  MDSListBox,
  MDSText,
  MDSTextWeight,
  MDSTextVariant,
  MDSIconKind,
  MDSIcon,
  MDSIconColor,
} from "@/mds";
import { MDSListBoxItemProps } from "@/mds/components/lists/list-box-item";
import { IntegrationsPageContentForm } from "@/pages/integrations/shared/integrations-form";
import { DeleteItemModal } from "@/pages/integrations/shared/integrations-delete-modal";
import { useAppStore } from "@/store";
import { EmailSenderObservable } from "@/store/email-senders/EmailSenderObservable";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useState } from "react";
import { TextMessageSenderObservable } from "@/store/text-message-senders/TextMessageSenderObservable";
import { IntegrationItemKind } from "@/pages/integrations/integrations-page.types";
import { IntegrationsFormatting } from "@/pages/integrations/shared/integrations-formatting";
import {
  StyledContentListTextContainer,
  StyledHeading,
} from "@/pages/integrations/shared/integrations.styles";

type Props = {
  itemKind: IntegrationItemKind;
};

type IntegrationItemType = EmailSenderObservable | TextMessageSenderObservable;

export const IntegrationsSenderItemList: FC<Props> = observer(({ itemKind }) => {
  const { store } = useAppStore();
  const [itemToDelete, setItemToDelete] = useState<IntegrationItemType | null>(null);

  const integrationStore = itemKind === "email" ? store.emailSenders : store.textMessageSenders;

  const { unVerifiedItem, addUnverifiedItem } = integrationStore;
  const label = itemKind === "email" ? "Verified Emails" : "Verified Phone Numbers";

  const { items } = getItems({
    data: integrationStore.items,
    setItemToDelete,
    unVerifiedItem,
    addUnverifiedItem,
    itemKind,
  });

  return (
    <Wrapper listHasItems={items.length > 0}>
      <StyledHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
        {label}
      </StyledHeading>
      <MDSListBox items={items} />
      <DeleteItemModal itemToDelete={itemToDelete} setItemToDelete={setItemToDelete} />

      {!!unVerifiedItem && (
        <IntegrationsPageContentForm hasItems={items.length > 0} unVerifiedItem={unVerifiedItem} />
      )}
      <IntegrationsFormatting itemKind={itemKind} />
    </Wrapper>
  );
});

const getItems = ({
  data,
  setItemToDelete,
  unVerifiedItem,
  addUnverifiedItem,
  itemKind,
}: {
  data: IntegrationItemType[];
  setItemToDelete: (item: IntegrationItemType) => void;
  unVerifiedItem: IntegrationItemType | undefined;
  addUnverifiedItem: () => void;
  itemKind: IntegrationItemKind;
}) => {
  const isFormOpen = !!unVerifiedItem;
  const label = itemKind === "email" ? "email" : "phone number";
  const iconKind = itemKind === "email" ? MDSIconKind.Envelope : MDSIconKind.Phone;

  const list: IntegrationItemType[] = data.filter(item => item.id !== unVerifiedItem?.id);

  const items: MDSListBoxItemProps[] = list.map(info => ({
    id: info.id,
    textValue: info.input,
    leftAddon: (
      <UserIconWrapper>
        <MDSIcon kind={iconKind} color={MDSIconColor.Secondary} />
      </UserIconWrapper>
    ),
    children: (
      <StyledContentListTextContainer>
        <MDSText variant={MDSTextVariant.TextS}>{info.input}</MDSText>
        <MDSIcon kind={MDSIconKind.CheckCircleSolid} color={MDSIconColor.Positive} />
      </StyledContentListTextContainer>
    ),
    rightAddon: info.canDelete ? (
      <MDSIconButton
        onClick={() => setItemToDelete(info)}
        aria-label={`Delete ${label}`}
        iconKind={MDSIconKind.Trash}
      />
    ) : null,
  }));

  if (!isFormOpen) {
    items.push({
      id: "create-integration-sender",
      textValue: `Connect ${list.length ? "another" : ""}${label}`,
      onAction: addUnverifiedItem,
      leftAddon: (
        <IconWrapper>
          <MDSIcon kind={MDSIconKind.Plus} color={MDSIconColor.Secondary} size={16} />
        </IconWrapper>
      ),
      children: (
        <MDSText variant={MDSTextVariant.TextS}>
          Connect {list.length ? "another " : ""}
          {label}
        </MDSText>
      ),
    });
  }

  return { items };
};

const Wrapper = styled.div<{ listHasItems: boolean }>(({ theme, listHasItems }) => ({
  width: "100%",
  marginTop: theme.spacing["2"],
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: listHasItems ? theme.spacing["2"] : theme.spacing["1"],
}));

const UserIconWrapper = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing[2],
}));

const IconWrapper = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing[2],
  height: 32,
  width: 32,
}));
